import React, { useCallback, useEffect, useState } from "react";
import { FiTrash2, FiX, FiCheck, FiSlash, FiHeart } from "react-icons/fi";
import { apiRequest, errorMessage, formatMoney } from "../../api";
import { CATEGORIES, embedUrl } from "../../components/foundation/foundationUtils";

const inputClass = "w-full px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white text-sm focus:outline-none focus:border-accent-light";
const STATUS_STYLE = {
  pending: "bg-amber-500/15 text-amber-400", approved: "bg-emerald-500/15 text-emerald-400",
  rejected: "bg-red-500/15 text-red-400", fulfilled: "bg-violet-500/15 text-violet-400",
};
const STATUS_LABEL = { pending: "Pending review", approved: "Approved", rejected: "Rejected", fulfilled: "Fulfilled" };

function Detail({ label, children }) {
  return (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm text-white">{children || "—"}</p>
    </div>
  );
}

function ReviewPanel({ item, onClose, onChanged }) {
  const [draft, setDraft] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setDraft({
      title: item.title, category: item.category, amount_needed: item.amount_needed, story: item.story,
      status: item.status, admin_note: item.admin_note, video_url: item.video_url,
    });
    setMessage("");
    setError("");
  }, [item]);

  if (!draft) return null;
  const set = (field) => (e) => setDraft({ ...draft, [field]: e.target.value });

  const save = async (overrides = {}) => {
    setMessage("");
    setError("");
    const { ok, data } = await apiRequest(`/core/foundation/cases/${item.id}/`, { method: "PATCH", auth: true, body: { ...draft, ...overrides } });
    if (!ok) return setError(errorMessage(data));
    setMessage(overrides.status ? `Marked as ${STATUS_LABEL[overrides.status].toLowerCase()}.` : "Saved.");
    onChanged();
  };

  const upload = async (fields) => {
    const fd = new FormData();
    Object.entries(fields).forEach(([k, v]) => (Array.isArray(v) ? v.forEach((f) => fd.append(k, f)) : fd.append(k, v)));
    return apiRequest(`/core/foundation/cases/${item.id}/`, { method: "PATCH", auth: true, body: fd });
  };

  const replaceFile = async (field, file) => {
    if (!file) return;
    const { ok, data } = await upload({ [field]: file });
    if (!ok) return setError(errorMessage(data));
    onChanged();
  };

  const addPhotos = async (files) => {
    for (const file of files) {
      const fd = new FormData();
      fd.append("case", item.id);
      fd.append("image", file);
      const { ok, data } = await apiRequest("/core/foundation/case-images/", { method: "POST", auth: true, body: fd });
      if (!ok) return setError(errorMessage(data));
    }
    onChanged();
  };

  const removePhoto = async (photo) => {
    if (!window.confirm("Remove this photo?")) return;
    await apiRequest(`/core/foundation/case-images/${photo.id}/`, { method: "DELETE", auth: true });
    onChanged();
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete "${item.title}" and all its help offers?`)) return;
    await apiRequest(`/core/foundation/cases/${item.id}/`, { method: "DELETE", auth: true });
    onClose();
    onChanged();
  };

  const embed = embedUrl(item.video_url);

  return (
    <div className="bg-navy border border-accent-light/40 rounded-xl p-6 mb-6">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div>
          <h2 className="text-lg font-bold">Review request #{item.id}</h2>
          <span className={`inline-block mt-1 px-2.5 py-0.5 rounded-full text-xs ${STATUS_STYLE[item.status]}`}>{STATUS_LABEL[item.status]}</span>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white" aria-label="Close"><FiX size={20} /></button>
      </div>

      <div className="rounded-lg bg-white/5 border border-navy-border p-4 mb-5">
        <p className="text-xs text-amber-300 mb-3">Private - applicant details are never shown publicly.</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Detail label="Full name">{item.full_name}</Detail>
          <Detail label="Phone">{item.phone}</Detail>
          <Detail label="Email">{item.email}</Detail>
          <Detail label="City / Country">{[item.city, item.country].filter(Boolean).join(", ")}</Detail>
          <Detail label="Age">{item.age}</Detail>
          <Detail label="Submitted">{new Date(item.created_at).toLocaleString()}</Detail>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-400 mb-1">Title (public)</label>
          <input className={inputClass} value={draft.title} onChange={set("title")} />
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Category</label>
          <select className={`${inputClass} bg-navy-dark`} value={draft.category} onChange={set("category")}>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-400 mb-1">Amount needed (0 = non-monetary)</label>
          <input type="number" min="0" step="0.01" className={inputClass} value={draft.amount_needed} onChange={set("amount_needed")} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-400 mb-1">Story (public)</label>
          <textarea rows={6} className={inputClass} value={draft.story} onChange={set("story")} />
        </div>
        <div className="md:col-span-2">
          <label className="block text-xs text-gray-400 mb-1">Internal note (private) - verification details, reason for rejection...</label>
          <textarea rows={3} className={inputClass} value={draft.admin_note} onChange={set("admin_note")} />
        </div>
      </div>

      <div className="mt-5">
        <p className="text-sm font-semibold mb-2">Photos</p>
        <div className="flex flex-wrap gap-3">
          {item.cover_image && (
            <div className="relative">
              <img src={item.cover_image} alt="Main" className="w-28 h-24 object-cover rounded-md border border-accent-light" />
              <span className="absolute bottom-1 left-1 px-1.5 rounded bg-navy/80 text-[10px]">Main</span>
            </div>
          )}
          {item.images.map((img) => (
            <div key={img.id} className="relative">
              <img src={img.image} alt="" className="w-28 h-24 object-cover rounded-md border border-navy-border" />
              <button onClick={() => removePhoto(img)} className="absolute top-1 right-1 w-6 h-6 flex items-center justify-center rounded-full bg-red-500/90 text-white" aria-label="Remove photo"><FiX size={12} /></button>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-6 mt-3 text-xs text-gray-400">
          <label>{item.cover_image ? "Replace" : "Add"} main photo: <input type="file" accept="image/*" onChange={(e) => replaceFile("cover_image", e.target.files[0])} className="ml-1" /></label>
          <label>Add photos: <input type="file" accept="image/*" multiple onChange={(e) => addPhotos([...e.target.files])} className="ml-1" /></label>
        </div>
      </div>

      <div className="mt-5">
        <p className="text-sm font-semibold mb-2">Video</p>
        {item.video && <video src={item.video} controls className="w-full max-w-md rounded-lg bg-black mb-2" />}
        {embed && <div className="aspect-video max-w-md rounded-lg overflow-hidden bg-black mb-2"><iframe src={embed} title="Video" className="w-full h-full" allowFullScreen /></div>}
        <div className="grid md:grid-cols-2 gap-3 items-end">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Video link (YouTube / Vimeo)</label>
            <input type="url" className={inputClass} value={draft.video_url} onChange={set("video_url")} />
          </div>
          <label className="text-xs text-gray-400">{item.video ? "Replace" : "Upload"} video file: <input type="file" accept="video/*" onChange={(e) => replaceFile("video", e.target.files[0])} className="ml-1" /></label>
        </div>
      </div>

      {error && <p className="text-sm text-red-400 mt-4">{error}</p>}
      {message && <p className="text-sm text-green-400 mt-4">{message}</p>}

      <div className="flex flex-wrap items-center gap-2 mt-6 pt-5 border-t border-navy-border">
        <button onClick={() => save({ status: "approved" })} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 text-white text-sm font-semibold"><FiCheck /> Approve &amp; Publish</button>
        <button onClick={() => save({ status: "rejected" })} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-red-600/90 text-white text-sm font-semibold"><FiSlash /> Reject</button>
        <button onClick={() => save({ status: "fulfilled" })} className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-violet-600 text-white text-sm font-semibold"><FiHeart /> Mark Fulfilled</button>
        <button onClick={() => save()} className="px-4 py-2 rounded-md bg-brand-gradient text-white text-sm font-semibold">Save Changes</button>
        <button onClick={remove} className="ml-auto flex items-center gap-1.5 px-3 py-2 rounded-md text-red-400 hover:bg-red-500/10 text-sm"><FiTrash2 size={14} /> Delete</button>
      </div>
    </div>
  );
}

export default function FoundationRequestsAdmin() {
  const [items, setItems] = useState(null);
  const [status, setStatus] = useState("pending");
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    const { ok, data } = await apiRequest(`/core/foundation/cases/${status ? `?status=${status}` : ""}`, { auth: true });
    setItems(ok ? data : []);
  }, [status]);

  useEffect(() => { setSelectedId(null); load(); }, [load]);

  const selected = (items || []).find((i) => i.id === selectedId);

  return (
    <div className="w-full">
      <h1 className="text-2xl font-bold mb-2">Help Requests</h1>
      <p className="text-sm text-gray-400 mb-6">
        Requests submitted by people in need. Review the details, photos and video, then approve to publish the card on the
        Foundation page. Only approved requests are visible to the public.
      </p>

      <div className="flex flex-wrap gap-2 mb-5">
        {[["pending", "Pending review"], ["approved", "Approved"], ["fulfilled", "Fulfilled"], ["rejected", "Rejected"], ["", "All"]].map(([key, label]) => (
          <button key={key} onClick={() => setStatus(key)} className={`px-4 py-1.5 rounded-full text-sm ${status === key ? "bg-brand-gradient text-white" : "bg-white/5 border border-navy-border text-gray-300"}`}>
            {label}
          </button>
        ))}
      </div>

      {selected && <ReviewPanel item={selected} onClose={() => setSelectedId(null)} onChanged={load} />}

      <div className="bg-navy border border-navy-border rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/5 text-gray-400 text-left">
            <tr>
              <th className="px-4 py-3 w-24">Photo</th>
              <th className="px-4 py-3">Request</th>
              <th className="px-4 py-3">Needed / Raised</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Helpers</th>
              <th className="px-4 py-3 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {(items || []).map((item) => {
              const thumb = item.cover_image || item.images[0]?.image;
              return (
                <tr key={item.id} className={`border-t border-navy-border align-middle ${item.id === selectedId ? "bg-accent/10" : ""}`}>
                  <td className="px-4 py-3">
                    {thumb ? <img src={thumb} alt="" className="w-16 h-12 object-cover rounded-md border border-navy-border" /> : <div className="w-16 h-12 rounded-md bg-white/5 border border-navy-border" />}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-white font-medium">{item.title}</p>
                    <p className="text-xs text-gray-500">{item.full_name} · {item.category_label} · {new Date(item.created_at).toLocaleDateString()}</p>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-300">
                    {Number(item.amount_needed) > 0 ? <>{formatMoney(item.amount_needed)}<br /><span className="text-gray-500">{formatMoney(item.amount_raised)} raised</span></> : "Non-monetary"}
                  </td>
                  <td className="px-4 py-3"><span className={`px-2.5 py-1 rounded-full text-xs ${STATUS_STYLE[item.status]}`}>{STATUS_LABEL[item.status]}</span></td>
                  <td className="px-4 py-3 text-xs text-gray-300">{item.offers_count} offer{item.offers_count === 1 ? "" : "s"}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => { setSelectedId(item.id); window.scrollTo({ top: 0, behavior: "smooth" }); }} className="px-3 py-1 rounded-md bg-brand-gradient text-white text-xs font-semibold">Review</button>
                  </td>
                </tr>
              );
            })}
            {items && items.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-gray-500">Nothing here.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
